/**
 * THE FOUR COUNTERS — shape, wire coercion, and owner-facing wording.
 *
 * DEPENDENCY-FREE ON PURPOSE, and that is load-bearing. This module is imported
 * by BOTH lanes: the engine (workers/counters.ts derives the numbers) and the
 * dashboard (src/lib/api.ts reads them off the wire, Dashboard.tsx renders
 * them). One convenience import of a D1 type here would drag D1Database into
 * the frontend's tsc graph and break the Pages build — the failure that forced
 * workers/sentinel.ts out of capture.ts and workers/volatility.ts out of
 * facts.ts. Third time is not the charm; keep this file free of every runtime
 * type.
 *
 * WHY THE WORDING LIVES HERE AND NOT IN THE COMPONENT. These four numbers have
 * one dangerous property: several of their states share a digit. 0/0 answered
 * is "nobody asked", not "nothing was answered". A counter whose emitter has not
 * shipped reads 0 whether the product triaged nothing or was never switched on.
 * A zero with two meanings is a lie wearing a number, and the owner is being
 * asked to judge his product by these. So the rules that turn a counter into
 * words live in one tested place, not in JSX where nothing can assert them.
 *
 * The component's whole job is to render what summariseCounters() returns.
 */

/** The engine's monthly summary, as both lanes read it. */
export interface MonthlyCounters {
  tenantId: string
  /** Unix epoch seconds, inclusive start / exclusive end of the window. */
  fromSeconds: number
  toSeconds: number

  answerRate: {
    asked: number
    answered: number
    /** answered/asked, null when nothing was asked — 0/0 is "no traffic",
     *  not "0% answered", and the two must not render the same. */
    rate: number | null
  }

  gaps: {
    opened: number
    /** Attribution split — the number that says whether the second detector
     *  earned its cost. Keys are the committed signal enum. */
    openedBySignal: { sentinel: number; review: number }
    closed: number
    /** Gaps the owner binned without answering (gap_dismissed, A24). Counted
     *  apart from `closed`, which means ANSWERED and must never claim more. */
    dismissed: number
    /** Mean hours from gap_opened to gap_answered, for pairs whose CLOSE falls
     *  in the window. Null when nothing closed. */
    avgHoursToClose: number | null
  }

  /**
   * `live` is a committed constant today (Builds 4 and 8 have not shipped
   * emitters) and flips by code change when they do. It is typed `boolean`
   * rather than the literal `false` so the client's coercion can carry a true
   * one through the day it starts being sent, without the wire reader and the
   * engine disagreeing about the type of the same field.
   */
  enquiriesTriagedOut: { count: number; live: boolean }
  caseFiles: { created: number; outcomes: number; live: boolean }
}

/* ---------------------------------------------------------------------------
 * Wire coercion — never trust the wire, and never invent a number.
 * ------------------------------------------------------------------------- */

function asNumber(v: unknown): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : 0
}

/** A number that is allowed to be absent. Absent stays NULL — coercing a
 *  missing rate to 0 would turn "we don't know" into "you answered nothing". */
function asNullableNumber(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null
}

function asRecord(v: unknown): Record<string, unknown> {
  return (v ?? {}) as Record<string, unknown>
}

/**
 * Coerce the engine's JSON into MonthlyCounters.
 *
 * Two rules do the real work here, and both fail SAFE rather than flattering:
 *  - an absent/garbled rate stays null, never 0 (see asNullableNumber);
 *  - `live` is true only when the engine explicitly says true. An unknown or
 *    absent flag reads NOT live, so a counter nothing feeds can never render as
 *    a result the owner might act on.
 */
export function coerceCounters(raw: unknown): MonthlyCounters {
  const r = asRecord(raw)
  const answerRate = asRecord(r.answerRate)
  const gaps = asRecord(r.gaps)
  const signal = asRecord(gaps.openedBySignal)
  const triaged = asRecord(r.enquiriesTriagedOut)
  const cases = asRecord(r.caseFiles)

  return {
    tenantId: typeof r.tenantId === 'string' ? r.tenantId : '',
    fromSeconds: asNumber(r.fromSeconds),
    toSeconds: asNumber(r.toSeconds),
    answerRate: {
      asked: asNumber(answerRate.asked),
      answered: asNumber(answerRate.answered),
      rate: asNullableNumber(answerRate.rate),
    },
    gaps: {
      opened: asNumber(gaps.opened),
      openedBySignal: {
        sentinel: asNumber(signal.sentinel),
        review: asNumber(signal.review),
      },
      closed: asNumber(gaps.closed),
      // Absent on a report from an engine older than batch 2g: that engine never
      // recorded a dismissal, so 0 is the truth about what it logged.
      dismissed: asNumber(gaps.dismissed),
      avgHoursToClose: asNullableNumber(gaps.avgHoursToClose),
    },
    enquiriesTriagedOut: { count: asNumber(triaged.count), live: triaged.live === true },
    caseFiles: {
      created: asNumber(cases.created),
      outcomes: asNumber(cases.outcomes),
      live: cases.live === true,
    },
  }
}

/* ---------------------------------------------------------------------------
 * Wording.
 * ------------------------------------------------------------------------- */

/** One rendered number. The component reads these fields and nothing else. */
export interface CounterTile {
  key: 'answer_rate' | 'gaps' | 'triage' | 'case_files'
  /** Owner-facing heading. */
  label: string
  /** The headline. EM DASH when there is no number to state — never a stand-in
   *  zero, because a zero the owner reads as a result is a false report. */
  value: string
  /** One line saying what the headline means, including WHY it is a dash. */
  detail: string
  /** Optional second line — the split, the timing. Empty when there is none. */
  note: string
  /** False when nothing feeds this counter yet. The card dims it and the copy
   *  says so; it never renders as a measurement. */
  live: boolean
}

/** The dash. One constant so "no number" looks identical everywhere. */
export const NO_NUMBER = '—'

/**
 * Percentage wording.
 *
 * FLOOR, NOT ROUND, below the ceiling. Math.round(0.996 * 100) is 100, and
 * "100%" printed over a question that actually went unanswered is the engine
 * claiming a perfection its own log contradicts. Flooring means 100% appears
 * only when every asked question was answered.
 */
export function formatRate(rate: number): string {
  if (rate >= 1) return '100%'
  if (rate <= 0) return '0%'
  return `${Math.floor(rate * 100)}%`
}

/**
 * Time-to-answer wording, in the units a busy tradesperson thinks in. Bands
 * rather than decimals: "about 5 hours" is the answer to the question he
 * actually has ("am I getting to these?"); "4.83 hours" is a spreadsheet.
 */
export function formatHoursToClose(hours: number | null): string {
  if (hours === null || !Number.isFinite(hours) || hours < 0) return ''
  if (hours < 1) return 'usually within the hour'
  if (hours < 48) {
    const n = Math.round(hours)
    return `usually about ${n} ${n === 1 ? 'hour' : 'hours'}`
  }
  const days = Math.round(hours / 24)
  return `usually about ${days} ${days === 1 ? 'day' : 'days'}`
}

/** The window's month, in UTC — matching the engine's UTC calendar month. Read
 *  in local time it would name a different month either side of midnight on the
 *  1st, and a report that changes with the reader is not a report. */
export function formatWindowLabel(fromSeconds: number): string {
  if (!Number.isFinite(fromSeconds) || fromSeconds <= 0) return ''
  return new Date(fromSeconds * 1000).toLocaleDateString('en-GB', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`
}

/**
 * Turn the engine's numbers into the four tiles the dashboard renders.
 *
 * Every branch below exists because two different truths would otherwise share
 * one appearance. Read them as a list of things the owner must never be told:
 *   - "0%" when nobody asked anything;
 *   - "100%" when one question went unanswered;
 *   - "0 triaged" when triage has not shipped;
 *   - a signal split that adds up to fewer gaps than were opened, with nothing
 *     saying where the rest came from.
 */
export function summariseCounters(c: MonthlyCounters): CounterTile[] {
  const { asked, answered, rate } = c.answerRate

  const answerTile: CounterTile = {
    key: 'answer_rate',
    label: 'Answer rate',
    value: rate === null ? NO_NUMBER : formatRate(rate),
    detail:
      rate === null
        ? 'No one has asked anything yet this month.'
        : `${answered} of ${plural(asked, 'question', 'questions')} answered outright.`,
    note: '',
    live: true,
  }

  const attributed = c.gaps.openedBySignal.sentinel + c.gaps.openedBySignal.review
  const splitParts: string[] = []
  if (attributed > 0) {
    splitParts.push(`${c.gaps.openedBySignal.sentinel} spotted by the assistant`)
    splitParts.push(`${c.gaps.openedBySignal.review} by the second check`)
  }
  // Gaps logged before attribution existed count in the total and claim neither
  // detector. Saying so is the difference between a split and a guess.
  if (c.gaps.opened > attributed) {
    splitParts.push(`${c.gaps.opened - attributed} from before this was recorded`)
  }
  const timing = formatHoursToClose(c.gaps.avgHoursToClose)
  if (timing) splitParts.push(timing)

  const gapsTile: CounterTile = {
    key: 'gaps',
    label: 'Questions you were asked to answer',
    value: String(c.gaps.opened),
    detail:
      c.gaps.opened === 0
        ? 'Nothing came up that your assistant could not answer.'
        : c.gaps.dismissed > 0
          ? `${c.gaps.closed} answered so far, ${c.gaps.dismissed} dismissed.`
          : `${c.gaps.closed} answered so far.`,
    note: splitParts.join(' · '),
    live: true,
  }

  // Builds 4 and 8 have not shipped emitters. These read as a dash and say why,
  // rather than rendering a zero that reads "your assistant triaged nothing".
  const triageTile: CounterTile = {
    key: 'triage',
    label: 'Enquiries filtered out',
    value: c.enquiriesTriagedOut.live ? String(c.enquiriesTriagedOut.count) : NO_NUMBER,
    detail: c.enquiriesTriagedOut.live
      ? `${plural(c.enquiriesTriagedOut.count, 'enquiry', 'enquiries')} you did not have to deal with.`
      : 'Not switched on yet.',
    note: c.enquiriesTriagedOut.live ? '' : 'Arrives with enquiry filtering.',
    live: c.enquiriesTriagedOut.live,
  }

  const caseTile: CounterTile = {
    key: 'case_files',
    label: 'Job files put together',
    value: c.caseFiles.live ? String(c.caseFiles.created) : NO_NUMBER,
    detail: c.caseFiles.live
      ? `${plural(c.caseFiles.outcomes, 'outcome', 'outcomes')} recorded.`
      : 'Not switched on yet.',
    note: c.caseFiles.live ? '' : 'Arrives with job files.',
    live: c.caseFiles.live,
  }

  return [answerTile, gapsTile, triageTile, caseTile]
}
